import { useEffect, useRef } from 'react';

/**
 * KAN-1186 - "open this ONE record on top of where I am".
 *
 * A shortcut to a single record (customer, job, ...) must never switch the tab and then open
 * the record (the in-between page flashes). A role home that owns the record's overlay
 * registers an opener here; any pop-up / chat / hub calls `openRecordOnTop(kind, id)` and the
 * record opens over the current screen, which stays where it is. No opener registered (a role
 * that has none) -> returns false and the caller keeps its old path.
 *
 * Module-level registry (like openJobDetailRegistry): no provider, no prop chain, so session-level
 * hosts (the lifecycle dock) reach it too. Holds functions only; the home unmounts at sign-out
 * and unregisters. The opener must be tenant-scoped itself (tenant from the session, RLS applies).
 */
const openers = new Map(); // kind -> fn

export function registerRecordOpener(kind, fn) {
  if (!kind || typeof fn !== 'function') return () => {};
  openers.set(kind, fn);
  return () => {
    if (openers.get(kind) === fn) openers.delete(kind);
  };
}

/**
 * Resolves true when a registered opener took the request (even "not found" - it said so itself),
 * false when nobody can open this kind here (caller falls back).
 */
export async function openRecordOnTop(kind, id) {
  const fn = openers.get(kind);
  if (!fn || id == null || id === '') return false;
  try {
    const handled = await fn(String(id));
    return handled !== false;
  } catch {
    return true; // the opener owns its own error feedback; never fall back to a tab change
  }
}

export function hasRecordOpener(kind) {
  return openers.has(kind);
}

/** Registers `fn` for `kind` while mounted; always calls the latest `fn`. */
export function useRegisterRecordOpener(kind, fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => registerRecordOpener(kind, (id) => ref.current(id)), [kind]);
}

export function clearRecordOpeners() {
  openers.clear();
}
